import { useState, useEffect, useMemo } from 'react'
import { API_URL, sb } from '../utils/config'
import { backlinkDirectories, SUBMIT_URL } from '../data/backlinkDirectories'

// ════════════════════════════════════════════════════════════════
// AdminBacklinks — tracker de soumissions d'annuaires + copy prête à coller.
// Liste les annuaires (dofollow gratuits d'abord), statut persisté en Supabase
// (table backlink_submissions), barre de progression, et copy copiable champ
// par champ. Style aligné sur AdminDashboard (orange #fe8204, Panel/Kpi).
// ════════════════════════════════════════════════════════════════

const ORANGE = '#fe8204', GREEN = '#16a34a', BLUE = '#3b82f6', GREY = '#94a3b8'

const STATUSES = [
  { id: 'todo',      label: 'À faire',   color: GREY },
  { id: 'submitted', label: 'Soumis',    color: ORANGE },
  { id: 'live',      label: 'En ligne',  color: GREEN },
]

// Métadonnée d'affichage du type de lien.
function dofollowMeta(d) {
  if (d === true)     return { label: 'Dofollow', sub: 'gratuit', color: GREEN,  bg: 'rgba(22,163,74,0.10)' }
  if (d === 'paid')   return { label: 'Dofollow', sub: 'payant',  color: ORANGE, bg: 'rgba(254,130,4,0.12)' }
  if (d === 'cond')   return { label: 'Dofollow', sub: 'badge',   color: BLUE,   bg: 'rgba(59,130,246,0.12)' }
  return { label: 'Nofollow', sub: null, color: GREY, bg: 'rgba(148,163,184,0.14)' }
}

// Rang de tri : dofollow gratuit (0) < conditionnel (1) < payant (2) < nofollow (3).
function dofollowRank(d) {
  if (d === true) return 0
  if (d === 'cond') return 1
  if (d === 'paid') return 2
  return 3
}

async function getAuthHeaders() {
  const { data } = await sb.auth.getSession()
  const token = data?.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export default function AdminBacklinks() {
  // statusMap : { [directoryName]: { status, live_url } }
  const [statusMap, setStatusMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(null) // nom de l'annuaire déplié
  const [copied, setCopied] = useState(null) // clé "name:field" copiée

  async function load() {
    setLoading(true); setErr(null)
    try {
      const headers = await getAuthHeaders()
      const r = await fetch(`${API_URL}/api/admin/backlinks`, { headers })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      const map = {}
      for (const row of (Array.isArray(j) ? j : (j.rows || []))) {
        map[row.directory] = { status: row.status || 'todo', live_url: row.live_url || '' }
      }
      setStatusMap(map)
    } catch (e) { setErr(e.message) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function saveStatus(directory, patch) {
    const prev = statusMap[directory] || { status: 'todo', live_url: '' }
    const next = { ...prev, ...patch }
    setStatusMap(m => ({ ...m, [directory]: next })) // optimiste
    try {
      const headers = { 'Content-Type': 'application/json', ...(await getAuthHeaders()) }
      await fetch(`${API_URL}/api/admin/backlinks`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ directory, status: next.status, live_url: next.live_url || null }),
      })
    } catch (e) {
      setErr(e.message)
      setStatusMap(m => ({ ...m, [directory]: prev })) // rollback
    }
  }

  function copy(key, value) {
    try {
      navigator.clipboard.writeText(value)
      setCopied(key)
      setTimeout(() => setCopied(c => (c === key ? null : c)), 1400)
    } catch {}
  }

  // Tri : dofollow gratuits d'abord, puis priorité du fichier source.
  const sorted = useMemo(() => {
    return backlinkDirectories.slice().sort((a, b) => {
      const ra = dofollowRank(a.dofollow), rb = dofollowRank(b.dofollow)
      if (ra !== rb) return ra - rb
      return (a.priority || 99) - (b.priority || 99)
    })
  }, [])

  const total = backlinkDirectories.length
  const submittedCount = Object.values(statusMap).filter(s => s.status === 'submitted' || s.status === 'live').length
  const liveCount = Object.values(statusMap).filter(s => s.status === 'live').length
  const dofollowFreeCount = backlinkDirectories.filter(d => d.dofollow === true).length
  const progressPct = total ? Math.round((submittedCount / total) * 100) : 0

  if (loading) return <div className="py-16 text-center text-ink-muted text-sm">Chargement des annuaires…</div>

  return (
    <div className="space-y-5">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-ink tracking-tight">🔗 Backlinks — annuaires</h2>
          <p className="text-[11px] text-ink-muted mt-0.5">
            Soumets Merlin Studio aux annuaires. Copy prête à coller, statut persisté.
            URL à soumettre : <span className="font-mono text-ink">{SUBMIT_URL}</span>
          </p>
        </div>
        <button onClick={load} className="px-2.5 py-1.5 text-sm text-ink-muted hover:text-orange" title="Rafraîchir">↻</button>
      </div>

      {err && <p className="text-xs text-red-600">Erreur : {err}</p>}

      {/* ─── KPI + PROGRESSION ─── */}
      <div className="bg-white rounded-2xl border border-border p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Kpi label="Soumis" value={`${submittedCount}/${total}`} accent={ORANGE} />
          <Kpi label="En ligne" value={`${liveCount}/${total}`} accent={GREEN} />
          <Kpi label="Reste à faire" value={total - submittedCount} accent={BLUE} />
          <Kpi label="Dofollow gratuits" value={dofollowFreeCount} accent={GREEN} sub="vraie valeur SEO" />
        </div>
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-bold text-ink">Progression des soumissions</span>
          <span className="font-mono text-ink-muted">{progressPct}%</span>
        </div>
        <div className="h-3 rounded-full bg-ink/5 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, progressPct)}%`, background: `linear-gradient(90deg, ${ORANGE}, #f59e0b)` }} />
        </div>
      </div>

      {/* ─── LISTE ─── */}
      <div className="space-y-3">
        {sorted.map(d => {
          const st = statusMap[d.name] || { status: 'todo', live_url: '' }
          const meta = dofollowMeta(d.dofollow)
          const isOpen = open === d.name
          return (
            <div key={d.name} className="bg-white rounded-2xl border border-border overflow-hidden">
              {/* Ligne principale */}
              <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-extrabold text-ink">{d.name}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: meta.bg, color: meta.color }}>
                      {meta.label}{meta.sub ? ` · ${meta.sub}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <a href={d.submitUrl} target="_blank" rel="noreferrer"
                      className="text-[11px] font-bold text-orange hover:underline inline-flex items-center gap-1">
                      Ouvrir le formulaire ↗
                    </a>
                    <button onClick={() => setOpen(isOpen ? null : d.name)}
                      className="text-[11px] font-bold text-ink-muted hover:text-ink">
                      {isOpen ? 'Masquer la copy ▲' : 'Voir la copy ▼'}
                    </button>
                  </div>
                </div>

                {/* Sélecteur de statut */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {STATUSES.map(s => {
                    const active = st.status === s.id
                    return (
                      <button key={s.id} onClick={() => saveStatus(d.name, { status: s.id })}
                        className="px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all"
                        style={active
                          ? { background: s.color, color: '#fff', borderColor: s.color }
                          : { background: '#fff', color: '#64748b', borderColor: 'var(--border)' }}>
                        {s.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Champ URL en ligne quand "live" */}
              {st.status === 'live' && (
                <div className="px-4 pb-3 -mt-1">
                  <input
                    type="url"
                    value={st.live_url || ''}
                    onChange={e => setStatusMap(m => ({ ...m, [d.name]: { ...st, live_url: e.target.value } }))}
                    onBlur={e => saveStatus(d.name, { live_url: e.target.value })}
                    placeholder="URL de la fiche en ligne (pour suivre le backlink)"
                    className="w-full text-[11px] font-mono px-3 py-1.5 rounded-lg border border-border bg-bg/40 text-ink focus:outline-none focus:border-orange"
                  />
                </div>
              )}

              {/* Dépliant copy */}
              {isOpen && (
                <div className="border-t border-border bg-bg/30 p-4 space-y-3">
                  <CopyField label="Nom" value="Merlin Studio" k={`${d.name}:name`} copied={copied} onCopy={copy} />
                  <CopyField label="Tagline" value={d.copy.tagline} k={`${d.name}:tagline`} copied={copied} onCopy={copy} chars />
                  <CopyField label="Description courte" value={d.copy.shortDesc} k={`${d.name}:short`} copied={copied} onCopy={copy} chars multiline />
                  <CopyField label="Description longue" value={d.copy.longDesc} k={`${d.name}:long`} copied={copied} onCopy={copy} chars multiline />
                  <CopyField label="Catégorie" value={d.copy.category} k={`${d.name}:cat`} copied={copied} onCopy={copy} />
                  <CopyField label="Tags" value={d.copy.tags.join(', ')} k={`${d.name}:tags`} copied={copied} onCopy={copy} />
                  <CopyField label="URL à soumettre" value={SUBMIT_URL} k={`${d.name}:url`} copied={copied} onCopy={copy} />
                  {d.notes && (
                    <div className="text-[11px] text-ink-muted bg-amber-50/60 border border-amber-200 rounded-lg p-3 leading-relaxed">
                      <span className="font-bold text-amber-700">Notes :</span> {d.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Champ copiable ───
function CopyField({ label, value, k, copied, onCopy, chars, multiline }) {
  const isCopied = copied === k
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">
          {label}{chars ? <span className="text-ink-faint/70 normal-case font-mono ml-1.5">{value.length} car.</span> : null}
        </span>
        <button onClick={() => onCopy(k, value)}
          className="text-[10px] font-bold px-2 py-0.5 rounded-md border transition-all"
          style={isCopied
            ? { background: 'rgba(22,163,74,0.10)', color: GREEN, borderColor: 'rgba(22,163,74,0.30)' }
            : { background: '#fff', color: ORANGE, borderColor: 'rgba(254,130,4,0.30)' }}>
          {isCopied ? '✓ Copié' : 'Copier'}
        </button>
      </div>
      <div className={`text-[11px] text-ink bg-white border border-border rounded-lg px-3 py-2 ${multiline ? 'whitespace-pre-wrap max-h-44 overflow-y-auto' : 'truncate'}`}>
        {value}
      </div>
    </div>
  )
}

// ─── KPI card (aligné AdminDashboard) ───
function Kpi({ label, value, accent, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: accent }} />
      <div className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">{label}</div>
      <div className="text-2xl font-extrabold leading-none" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-1.5">{sub}</div>}
    </div>
  )
}
